import type {
  ExpenseCategory,
  ExpenseListParams,
  ExpenseReportParams,
  ExpenseSort,
  ExpenseStatus,
} from '@/api/types'
import { EXPENSE_CATEGORY_ORDER, EXPENSE_STATUS_ORDER } from '@/lib/orders'
import { csvOf, flag, integer, oneOf, orElse, readSearch, text } from '@/lib/search-params'

const STATUS_SET = new Set<string>(EXPENSE_STATUS_ORDER)
const CATEGORY_SET = new Set<string>(EXPENSE_CATEGORY_ORDER)
const PAID_WITH = ['personal_card', 'company_card'] as const

export const EXPENSE_SORTS = [
  '-expense_date',
  '-created_at',
  '-amount_cents',
  '-submitted_at',
  'number',
] as const satisfies readonly ExpenseSort[]

export const EXPENSE_SORT_LABELS: Record<ExpenseSort, string> = {
  '-expense_date': 'Date de la dépense, la plus récente d’abord',
  '-created_at': 'Saisie, la plus récente d’abord',
  '-amount_cents': 'Montant, le plus élevé d’abord',
  '-submitted_at': 'Soumission, la plus récente d’abord',
  number: 'Numéro',
}

/**
 * Filtres de la liste des notes de frais, portés par l'URL : « les repas de
 * septembre à rembourser » est un lien qu'on s'envoie. `statut` et
 * `categorie` sont des listes séparées par des virgules, comme le contrat
 * l'accepte ; `personne` n'a de sens que pour un administrateur.
 */
const SHAPE = {
  /** L'onglet « À rembourser » (administrateur) ; absent : la liste. */
  onglet: oneOf(['a-rembourser'] as const),
  q: text(),
  statut: csvOf(STATUS_SET),
  categorie: csvOf(CATEGORY_SET),
  personne: text(),
  paye: oneOf(PAID_WITH),
  client: text(),
  projet: text(),
  refacturable: flag(),
  du: text(),
  au: text(),
  corbeille: flag(),
  ordre: oneOf(EXPENSE_SORTS),
  page: orElse(integer({ min: 1 }), 1),
}

export type ExpensesSearch = ReturnType<typeof parseExpensesSearch>

export function parseExpensesSearch(search: Record<string, unknown>) {
  return readSearch(SHAPE, search)
}

export const EMPTY_EXPENSE_FILTERS: Partial<ExpensesSearch> = {
  q: undefined,
  statut: undefined,
  categorie: undefined,
  personne: undefined,
  paye: undefined,
  client: undefined,
  projet: undefined,
  refacturable: undefined,
  du: undefined,
  au: undefined,
  corbeille: undefined,
  ordre: undefined,
  page: 1,
}

export function selectedExpenseStatuses(search: ExpensesSearch): ExpenseStatus[] {
  return (search.statut?.split(',') ?? []).filter((status): status is ExpenseStatus =>
    STATUS_SET.has(status),
  )
}

export function selectedExpenseCategories(search: ExpensesSearch): ExpenseCategory[] {
  return (search.categorie?.split(',') ?? []).filter((category): category is ExpenseCategory =>
    CATEGORY_SET.has(category),
  )
}

export function hasActiveExpenseFilters(search: ExpensesSearch): boolean {
  return !!(
    search.q ??
    search.statut ??
    search.categorie ??
    search.personne ??
    search.paye ??
    search.client ??
    search.projet ??
    search.refacturable ??
    search.du ??
    search.au ??
    search.corbeille
  )
}

/**
 * Un membre ne nomme personne : le serveur ne lui rend que ses notes, et un
 * `user_id` qui n'est pas le sien vaut 403. Le paramètre ne part que d'un
 * administrateur.
 */
export function toExpenseListParams(
  search: ExpensesSearch,
  { pageSize, isAdmin }: { pageSize: number; isAdmin: boolean },
): ExpenseListParams {
  const q = search.q?.trim()
  return {
    q: q === '' ? undefined : q,
    status: search.statut,
    category: search.categorie,
    user_id: isAdmin ? search.personne : undefined,
    paid_with: search.paye,
    client_id: search.client,
    project_id: search.projet,
    rebillable: search.refacturable ? true : undefined,
    from: search.du,
    to: search.au,
    deleted: search.corbeille ? true : undefined,
    sort: search.ordre,
    page: search.page,
    page_size: pageSize,
  }
}

/** Le rapport prend la période, la personne, les statuts et le client de la même URL. */
export function toExpenseReportParams(
  search: ExpensesSearch,
  { isAdmin }: { isAdmin: boolean },
): ExpenseReportParams {
  return {
    from: search.du,
    to: search.au,
    user_id: isAdmin ? search.personne : undefined,
    status: search.statut,
    client_id: search.client,
  }
}
